import {
  TrendingUp,
  Users,
  Award,
  Heart,
  DollarSign,
  Clock,
  Target,
  Briefcase,
  Star,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";

const Impact = () => {
  const organizationalBenefits = [
    {
      icon: TrendingUp,
      title: "Productivity Increase",
      value: "25-40%",
      timeframe: "Within 6 months",
      description:
        "Measurable improvement in organizational productivity and efficiency",
    },
    {
      icon: Clock,
      title: "Process Efficiency",
      value: "30-50%",
      timeframe: "Reduction in operational delays",
      description: "Streamlined processes and reduced operational bottlenecks",
    },
    {
      icon: Award,
      title: "Quality Improvement",
      value: "20-35%",
      timeframe: "Reduction in errors",
      description: "Enhanced quality standards and reduced error rates",
    },
    {
      icon: DollarSign,
      title: "Cost Savings",
      value: "15-25%",
      timeframe: "Reduction in operational costs",
      description: "Significant cost optimization through improved efficiency",
    },
    {
      icon: Users,
      title: "Employee Retention",
      value: "25-40%",
      timeframe: "Reduction in turnover",
      description: "Higher employee satisfaction and reduced recruitment costs",
    },
  ];

  const individualBenefits = [
    {
      icon: Briefcase,
      title: "Career Advancement",
      value: "40-60%",
      description: "Participants receive promotions within 2 years",
    },
    {
      icon: DollarSign,
      title: "Salary Growth",
      value: "20-35%",
      description: "Average improvement within 18 months",
    },
    {
      icon: Target,
      title: "Skill Enhancement",
      value: "45-65%",
      description: "Improvement in technical competencies",
    },
    {
      icon: Users,
      title: "Leadership Effectiveness",
      value: "40-60%",
      description: "Improvement in leadership capabilities",
    },
    {
      icon: Heart,
      title: "Job Satisfaction",
      value: "45-65%",
      description: "Increase in satisfaction scores",
    },
  ];

  const customerResults = [
    {
      icon: Star,
      title: "Customer Satisfaction",
      value: "30-50%",
      description: "Increase in customer satisfaction scores",
    },
    {
      icon: Award,
      title: "Service Quality",
      value: "35-55%",
      description: "Improvement in service quality ratings",
    },
    {
      icon: Users,
      title: "Customer Retention",
      value: "20-35%",
      description: "Increase in customer loyalty and retention",
    },
    {
      icon: TrendingUp,
      title: "Revenue Growth",
      value: "15-25%",
      description: "Increase in customer-driven revenue",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="min-h-[55vh] md:min-h-[85vh] sm:min-h-[45vh] py-10 lg:py-12 md:py-6 relative overflow-hidden flex items-center justify-center bg-no-repeat bg-center bg-contain"
        id="impact"
        style={{
          backgroundImage:
            "url('https://i.pinimg.com/736x/f0/24/a2/f024a231fad08aa1c0cc1731d0380eca.jpg')",
          // "url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSYXE9SImLjychuMyKGqQd-JeDERZ5B_8EKDg&s')",
          backgroundSize: "contain",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Optional: Add an overlay for readability */}
        <div className="absolute inset-0 bg-blue-100 opacity-80 z-0"></div>

        <div className="container mx-auto text-center max-w-5xl relative z-10">
          <Badge
            variant="outline"
            className="mb-6 px-6 py-2 text-sm md:text-base border-blue-500 text-blue-700"
          >
            Measurable Results
          </Badge>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-blue-500">
            Have a Look at Our Impact Across Africa
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Transforming organizations and individuals through evidence-based
            training programs that deliver measurable results and lasting
            change.
          </p>
        </div>
      </section>

      {/* Organizational Impact */}
      <section className="py-10 lg:py-12 md:py-6 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Organizational Benefits
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our training programs deliver tangible results that directly
              impact your organization's bottom line.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {organizationalBenefits.map((benefit, index) => (
              <Card
                key={index}
                className="text-center shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out bg-white rounded-xl"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-16 h-16 bg-golden rounded-full flex items-center justify-center mx-auto">
                    <benefit.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-3xl font-medium text-primary">
                      {benefit.value}
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-medium">
                      {benefit.timeframe}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Impact */}
      <section className="py-16 lg:py-24 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Individual Benefits
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Personal and professional development outcomes that transform
              careers and lives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {individualBenefits.map((benefit, index) => (
              <Card
                key={index}
                className="text-center shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out bg-white rounded-xl"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-16 h-16 bg-blue-300 rounded-full flex items-center justify-center mx-auto">
                    <benefit.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-3xl font-normal text-golden">
                      {benefit.value}
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Excellence Results */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Customer Excellence Results
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Enhanced customer experiences and improved business relationships
              through better service delivery.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {customerResults.map((result, index) => (
              <Card
                key={index}
                className="text-center shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out bg-white rounded-xl"
              >
                <CardContent className="p-8 space-y-4">
                  <div className="w-16 h-16 bg-golden rounded-full flex items-center justify-center mx-auto">
                    <result.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-3xl font-sm text-primary">
                      {result.value}
                    </div>
                    <h3 className="font-semibold text-golden">
                      {result.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {result.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics Overview */}
      <section className="py-16 lg:py-24 bg-gradient-primary text-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-8 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Why Organizations Choose TCLA-X
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Our track record speaks for itself - consistent, measurable
              results across all sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4 border-2 border-white rounded-lg p-2">
              <div className="text-2xl lg:text-4xl font-bold">500+</div>
              <div className="text-white/90 font-medium">
                Organizations Trained
              </div>
              <div className="text-sm text-white/70">Across Africa</div>
            </div>

            <div className="text-center space-y-4 border-2 border-blue-600 rounded-lg p-2">
              <div className="text-2xl lg:text-4xl font-bold">15,000+</div>
              <div className="text-white/90 font-medium">
                Professionals Developed
              </div>
              <div className="text-sm text-white/70">
                Individual participants
              </div>
            </div>

            <div className="text-center space-y-4 border-2 border-white rounded-lg p-2">
              <div className="text-2xl lg:text-4xl font-bold">98%</div>
              <div className="text-white/90 font-medium">
                Client Satisfaction
              </div>
              <div className="text-sm text-white/70">
                Training effectiveness rating
              </div>
            </div>

            <div className="text-center space-y-4 border-2 border-blue-600 rounded-lg p-2">
              <div className="text-2xl lg:text-4xl font-bold">85%</div>
              <div className="text-white/90 font-medium">Repeat Clients</div>
              <div className="text-sm text-white/70">
                Long-term partnerships
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-16 lg:py-24 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Real Impact Stories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how our training programs have transformed organizations and
              careers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-subtle">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  Government Ministry
                </h3>
                <p className="text-muted-foreground">
                  "TCLA-X's leadership development program resulted in a 45%
                  improvement in our project delivery timelines and
                  significantly enhanced our team collaboration."
                </p>
                <div className="text-sm text-primary font-medium">
                  - Senior Administrator
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-subtle">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  Healthcare Organization
                </h3>
                <p className="text-muted-foreground">
                  "The patient care excellence training transformed our service
                  delivery, resulting in 60% improvement in patient satisfaction
                  scores."
                </p>
                <div className="text-sm text-primary font-medium">
                  - Hospital Director
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-subtle">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  Private Corporation
                </h3>
                <p className="text-muted-foreground">
                  "Our productivity increased by 35% after implementing TCLA-X's
                  management excellence framework across all departments."
                </p>
                <div className="text-sm text-primary font-medium">- CEO</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
  TCLA-X helps You Write Your Own Success Story
</h2>
<p className="text-lg text-muted-foreground max-w-2xl mx-auto">
  Join the growing community of organizations turning challenges into breakthroughs with TCLA-X.
</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/contact">Start Your Transformation</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/programs">View Our Programs</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Impact;
